import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { useFabricationStore } from '../../stores/fabricationStore';
import { useTintedBackground } from '../../hooks/useTintedBackground';
import { Spinner } from '../common/Spinner';
import { FiSend, FiTrash2, FiX, FiMessageSquare, FiMinimize2, FiMaximize2, FiMapPin, FiClipboard, FiMaximize, FiMinimize } from 'react-icons/fi';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

interface TestDriveMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
}

interface TestDrivePanelProps {
  systemPrompt: string | null;
  personaName: string;
  needsRecompile: boolean;
  onCompile: () => void;
}

function generateId(): string {
  return Math.random().toString(36).substring(2, 10);
}

const TestDrivePanel: React.FC<TestDrivePanelProps> = ({
  systemPrompt,
  personaName,
  needsRecompile,
  onCompile,
}) => {
  const { darkMode } = useTheme();
  const {
    sendTestDriveMessage,
    isLoading: storeLoading,
    state: forgeState,
    pendingTestDriveMessage,
    setPendingTestDriveMessage,
    activeProbeId,
    pendingJudgmentProbeId,
    pendingJudgmentResponse,
  } = useFabricationStore();

  const [isOpen, setIsOpen] = useState<boolean>(() =>
    localStorage.getItem('testDrivePanelOpen') === 'true'
  );
  const [isMinimized, setIsMinimized] = useState(true);
  const [isMaximized, setIsMaximized] = useState(false);
  const [isPinned, setIsPinned] = useState<boolean>(() =>
    localStorage.getItem('testDrivePanelPinned') === 'true'
  );
  const [messages, setMessages] = useState<TestDriveMessage[]>([]);
  const [input, setInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [judgmentNotes, setJudgmentNotes] = useState('');

  // Track whether user explicitly closed the panel (don't auto-reopen)
  const userClosedRef = useRef<boolean>(
    localStorage.getItem('testDrivePanelUserClosed') === 'true'
  );
  const prevSystemPromptRef = useRef<string | null>(systemPrompt ?? null);

  const chatEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // ── Probe chips ───────────────────────────────────────────────────────────
  const probeChips = useMemo(() => {
    if (!forgeState) return [];
    const chips: { label: string; message: string }[] = [];

    // Primary: from probes
    if (forgeState.probes?.length) {
      forgeState.probes.slice(0, 4).forEach(p => {
        chips.push({ label: p.label, message: p.input_prompt });
      });
      return chips;
    }

    // Fallback: from capabilities + tags
    const caps = forgeState.capabilities || {};
    if ((caps.creativity ?? 0) >= 7)
      chips.push({ label: 'Creative writing', message: 'Write me a short story opening set in a cyberpunk city.' });
    if ((caps.technical ?? 0) >= 7)
      chips.push({ label: 'System design', message: 'Explain a key trade-off in distributed system design.' });
    if ((caps.communication ?? 0) >= 7)
      chips.push({ label: 'Feedback', message: 'Give me feedback on this sentence: "The system is very fast and good."' });
    if (chips.length < 2 && forgeState.tags?.length) {
      chips.push({ label: `About your expertise`, message: `Tell me about your area of expertise in ${forgeState.tags[0]}.` });
    }
    if (chips.length === 0)
      chips.push({ label: 'Introduce yourself', message: 'Introduce yourself and describe how you can help me.' });

    return chips.slice(0, 4);
  }, [forgeState]);

  // ── Active non-default controls subtitle ──────────────────────────────────
  const activeControlsSummary = useMemo(() => {
    if (!forgeState?.ui_schema?.length) return null;
    const nonDefault = forgeState.ui_schema.filter(
      c => c.current_value !== c.default_value
    );
    if (!nonDefault.length) return null;
    const parts = nonDefault.slice(0, 3).map(c => {
      const val = typeof c.current_value === 'boolean'
        ? (c.current_value ? 'ON' : 'OFF')
        : typeof c.current_value === 'number'
          ? c.current_value.toFixed(2).replace(/\.?0+$/, '')
          : String(c.current_value);
      return `${c.label}: ${val}`;
    });
    const extra = nonDefault.length > 3 ? ` +${nonDefault.length - 3} more` : '';
    return parts.join(' · ') + extra;
  }, [forgeState?.ui_schema]);

  // ── Tinted background — blue tint to match persona forge theme ────────────
  const tintedBg = useTintedBackground({
    tintColor: darkMode ? '#3b82f6' : '#2563eb',
    tintOpacity: 0.06,
    enabled: true,
  });

  // Persist open/closed/pinned state
  useEffect(() => {
    localStorage.setItem('testDrivePanelOpen', isOpen.toString());
  }, [isOpen]);

  useEffect(() => {
    localStorage.setItem('testDrivePanelPinned', isPinned.toString());
  }, [isPinned]);

  // Auto-open when systemPrompt first becomes available (first compile)
  useEffect(() => {
    const prev = prevSystemPromptRef.current;
    prevSystemPromptRef.current = systemPrompt;

    if (!prev && systemPrompt && !userClosedRef.current) {
      setIsOpen(true);
      setIsMinimized(true);
    }
  }, [systemPrompt]);

  const handleClose = useCallback(() => {
    if (isPinned) return; // pinned panels cannot be closed
    userClosedRef.current = true;
    localStorage.setItem('testDrivePanelUserClosed', 'true');
    setIsOpen(false);
  }, [isPinned]);

  const handleOpen = useCallback(() => {
    userClosedRef.current = false;
    localStorage.setItem('testDrivePanelUserClosed', 'false');
    setIsOpen(true);
    setIsMinimized(false);
  }, []);

  const handleTogglePin = useCallback(() => {
    setIsPinned(prev => {
      const next = !prev;
      if (next) {
        setIsOpen(true);
        setIsMinimized(false);
        userClosedRef.current = false;
        localStorage.setItem('testDrivePanelUserClosed', 'false');
      }
      return next;
    });
  }, []);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (isOpen && chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isOpen]);

  // Focus input when panel opens
  useEffect(() => {
    if (isOpen && systemPrompt && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen, systemPrompt]);

  // ── Bridge: fire pending message from ProbesPanel / external callers ──────
  useEffect(() => {
    if (!pendingTestDriveMessage || isSending || !systemPrompt) return;
    const msg = pendingTestDriveMessage;
    setPendingTestDriveMessage(null);
    setIsMinimized(false);
    if (!isOpen) handleOpen();
    handleSendWithContent(msg);
  }, [pendingTestDriveMessage, isSending, systemPrompt]);

  const handleSendWithContent = async (content: string) => {
    if (!content.trim() || isSending || !systemPrompt) return;
    const userMessage: TestDriveMessage = {
      id: generateId(),
      role: 'user',
      content: content.trim(),
    };
    setMessages(prev => [...prev, userMessage]);
    setIsSending(true);
    try {
      const reply = await sendTestDriveMessage(userMessage.content);
      setMessages(prev => [...prev, {
        id: generateId(),
        role: 'assistant',
        content: reply || '(no response)',
      }]);
      // If we were running a probe, hand the response to the store for judgment
      if (activeProbeId) {
        useFabricationStore.setState(s => ({
          pendingJudgmentProbeId: activeProbeId,
          pendingJudgmentResponse: reply || '(no response)',
          probeRunStates: { ...s.probeRunStates, [activeProbeId]: 'awaiting_judgment' },
          activeProbeId: null,
        }));
      }
    } catch {
      setMessages(prev => [...prev, {
        id: generateId(), role: 'assistant', content: 'Error: could not get a response.',
      }]);
      if (activeProbeId) {
        useFabricationStore.setState(s => ({
          probeRunStates: { ...s.probeRunStates, [activeProbeId]: 'idle' },
          activeProbeId: null,
        }));
      }
    } finally {
      setIsSending(false);
    }
  };

  const handleSend = async () => {
    if (!input.trim() || isSending || !systemPrompt) return;

    const userMessage: TestDriveMessage = {
      id: generateId(),
      role: 'user',
      content: input.trim(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsSending(true);

    try {
      const reply = await sendTestDriveMessage(userMessage.content);
      const assistantMessage: TestDriveMessage = {
        id: generateId(),
        role: 'assistant',
        content: reply || '(no response)',
      };
      setMessages(prev => [...prev, assistantMessage]);
    } catch {
      setMessages(prev => [
        ...prev,
        { id: generateId(), role: 'assistant', content: 'Error: could not get a response.' },
      ]);
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClear = () => {
    setMessages([]);
    setShowClearConfirm(false);
  };

  // Pill button state
  const hasPrompt = !!systemPrompt;
  const pillColor = !hasPrompt
    ? darkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'
    : isOpen
      ? darkMode ? 'bg-gray-600 text-white' : 'bg-gray-300 text-gray-800'
      : needsRecompile
        ? 'bg-amber-500 text-white'
        : 'bg-blue-600 text-white';

  const pillTitle = !hasPrompt
    ? 'Compile your persona first'
    : needsRecompile
      ? 'Prompt is stale — recompile for best results'
      : 'Open Test Drive';

  return (
    <>
      {/* Floating panel */}
      {isOpen && (
        <div
          ref={tintedBg.ref}
          className={`fixed flex flex-col rounded-xl shadow-2xl border transition-all duration-300 ${
            isMinimized ? 'h-16' : isMaximized ? 'h-[calc(100vh-2rem)]' : 'h-[calc(100vh-6rem)]'
          } ${
            darkMode
              ? 'bg-gray-800/95 border-blue-700/50 backdrop-blur-sm'
              : 'bg-white/95 border-blue-300/50 backdrop-blur-sm'
          }`}
          style={{
            ...tintedBg.style,
            position: 'fixed',
            bottom: '1rem',
            left: isMaximized ? '1rem' : '1rem',
            width: isMaximized ? 'calc(100vw - 2rem)' : '768px',
            zIndex: 9999,
          }}
        >
          {/* Tinted overlay — same pattern as ChatWithCanvas */}
          <div
            className="absolute inset-0 pointer-events-none rounded-xl"
            style={tintedBg.overlayStyle}
          />

          {/* Header — gradient style matching ChatWithCanvas */}
          <div className={`flex items-center justify-between px-4 py-3 rounded-t-xl border-b relative z-30 backdrop-blur-sm ${
            darkMode
              ? 'bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 border-gray-600 shadow-lg'
              : 'bg-gradient-to-r from-white via-gray-50 to-white border-gray-300 shadow-md'
          }`}>
            <div className="flex items-center gap-3 min-w-0">
              {activeProbeId && (
                <div className={`px-2 py-0.5 rounded text-xs font-medium flex items-center gap-1 flex-shrink-0 max-w-[160px] ${
                  darkMode ? 'bg-amber-800/50 text-amber-300' : 'bg-amber-100 text-amber-700'
                }`}>
                  <span className="flex-shrink-0">🔬</span>
                  <span className="truncate">
                    {forgeState?.probes?.find(p => p.id === activeProbeId)?.label ?? 'Probe mode'}
                  </span>
                </div>
              )}
              <div className={`flex-shrink-0 p-2 rounded-lg shadow-md ${
                darkMode
                  ? 'bg-gradient-to-br from-blue-600 to-blue-700 shadow-blue-500/20'
                  : 'bg-gradient-to-br from-blue-500 to-blue-600 shadow-blue-400/30'
              }`}>
                <FiMessageSquare size={14} className="text-white" />
              </div>
              <div className="min-w-0">
                <h3 className={`font-bold text-sm tracking-wide ${
                  darkMode
                    ? 'text-transparent bg-clip-text bg-gradient-to-r from-blue-300 to-purple-300'
                    : 'text-transparent bg-clip-text bg-gradient-to-r from-blue-700 to-purple-700'
                }`}>
                  Test Drive
                </h3>
                {personaName && (
                  <p className={`text-xs font-medium truncate ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                    {personaName}
                  </p>
                )}
                {activeControlsSummary && (
                  <p className={`text-xs truncate mt-0.5 ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}
                     title={activeControlsSummary}>
                    {activeControlsSummary}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {/* Export transcript */}
              {!isMinimized && messages.length > 0 && (
                <button
                  onClick={() => {
                    const nonDefault = forgeState?.ui_schema?.filter(
                      c => c.current_value !== c.default_value
                    ) || [];
                    const controlsLine = nonDefault.length
                      ? `**Controls (non-default):** ${nonDefault.map(c => `${c.label}: ${c.current_value}`).join(', ')}\n\n`
                      : '';
                    const transcript = [
                      `# Test Drive: ${personaName}`,
                      `**Date:** ${new Date().toISOString().split('T')[0]}`,
                      controlsLine,
                      '---',
                      '',
                      ...messages.map(m =>
                        `**${m.role === 'user' ? 'You' : personaName}:** ${m.content}`
                      ),
                    ].join('\n');
                    navigator.clipboard?.writeText(transcript).catch(() => {
                      const ta = document.createElement('textarea');
                      ta.value = transcript;
                      ta.style.position = 'fixed';
                      ta.style.left = '-999999px';
                      document.body.appendChild(ta);
                      ta.select();
                      document.execCommand('copy');
                      document.body.removeChild(ta);
                    });
                  }}
                  title="Export transcript to clipboard"
                  className={`p-1.5 rounded-full transition-all duration-200 ${
                    darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-200 text-gray-500'
                  }`}
                >
                  <FiClipboard size={14} />
                </button>
              )}
              {/* Clear button — only when not minimized */}
              {!isMinimized && messages.length > 0 && (
                showClearConfirm ? (
                  <div className="flex items-center gap-1 text-xs">
                    <button
                      onClick={handleClear}
                      className="px-2 py-0.5 rounded bg-red-600 text-white hover:bg-red-500"
                    >
                      Yes
                    </button>
                    <button
                      onClick={() => setShowClearConfirm(false)}
                      className={`px-2 py-0.5 rounded ${
                        darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
                      }`}
                    >
                      No
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setShowClearConfirm(true)}
                    title="Clear conversation"
                    className={`p-1.5 rounded-full transition-all duration-200 ${
                      darkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-200 text-gray-500'
                    }`}
                  >
                    <FiTrash2 size={14} />
                  </button>
                )
              )}
              {/* Pin button */}
              <button
                onClick={handleTogglePin}
                title={isPinned ? 'Unpin panel' : 'Pin panel open'}
                className={`p-1.5 rounded-full transition-all duration-200 ${
                  isPinned
                    ? darkMode
                      ? 'bg-blue-700 text-blue-200 shadow-md shadow-blue-500/30'
                      : 'bg-blue-100 text-blue-700 shadow-md shadow-blue-300/30'
                    : darkMode
                      ? 'hover:bg-gray-700 text-gray-400'
                      : 'hover:bg-gray-200 text-gray-500'
                }`}
              >
                <FiMapPin size={14} className={isPinned ? '' : 'rotate-45'} />
              </button>
              {/* Expand/Collapse (maximize) */}
              {!isMinimized && (
                <button
                  onClick={() => setIsMaximized(prev => !prev)}
                  title={isMaximized ? 'Restore size' : 'Expand panel'}
                  className={`p-1.5 rounded-full transition-all duration-200 ${
                    isMaximized
                      ? darkMode
                        ? 'bg-blue-700 text-blue-200 shadow-md shadow-blue-500/30'
                        : 'bg-blue-100 text-blue-700 shadow-md shadow-blue-300/30'
                      : darkMode
                        ? 'hover:bg-gray-700 text-gray-400'
                        : 'hover:bg-gray-200 text-gray-500'
                  }`}
                >
                  {isMaximized ? <FiMinimize size={14} /> : <FiMaximize size={14} />}
                </button>
              )}
              {/* Minimize/Restore */}
              <button
                onClick={() => {
                  setIsMinimized(prev => !prev);
                  if (isMaximized) setIsMaximized(false);
                }}
                title={isMinimized ? 'Restore' : 'Minimize'}
                className={`p-1.5 rounded-full transition-all duration-200 ${
                  darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-200'
                }`}
              >
                {isMinimized
                  ? <FiMaximize2 size={14} />
                  : <FiMinimize2 size={14} />
                }
              </button>
              {/* Close button — disabled when pinned */}
              <button
                onClick={handleClose}
                title={isPinned ? 'Unpin to close' : 'Close'}
                disabled={isPinned}
                className={`p-1.5 rounded-full transition-all duration-200 ${
                  isPinned
                    ? 'opacity-30 cursor-not-allowed'
                    : darkMode
                      ? 'hover:bg-red-900/50 text-red-400'
                      : 'hover:bg-red-100 text-red-600'
                }`}
              >
                <FiX size={14} />
              </button>
            </div>
          </div>

          {/* Messages gradient fade — same as ChatWithCanvas */}
          {!isMinimized && (
            <div className={`absolute inset-0 pointer-events-none z-20 ${
              darkMode
                ? 'bg-gradient-to-b from-gray-800/95 via-transparent to-transparent'
                : 'bg-gradient-to-b from-white/95 via-transparent to-transparent'
            }`} />
          )}

          {/* Stale banner */}
          {!isMinimized && needsRecompile && (
            <div className={`px-4 py-2 text-xs flex items-center gap-2 ${
              darkMode ? 'bg-amber-900/30 text-amber-400' : 'bg-amber-50 text-amber-700'
            }`}>
              <span>⚠ Prompt is stale — responses may not reflect latest changes.</span>
              <button
                onClick={onCompile}
                disabled={storeLoading}
                className={`ml-auto underline font-medium flex-shrink-0 ${
                  darkMode ? 'text-amber-300 hover:text-amber-200' : 'text-amber-700 hover:text-amber-900'
                }`}
              >
                Recompile
              </button>
            </div>
          )}

          {/* No prompt empty state */}
          {!isMinimized && !systemPrompt ? (
            <div className="flex-1 flex items-center justify-center p-6 text-center">
              <div>
                <p className={`text-sm mb-3 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                  Compile your persona first to start testing.
                </p>
                <button
                  onClick={onCompile}
                  disabled={storeLoading}
                  className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium"
                >
                  {storeLoading ? <Spinner size="sm" /> : 'Compile Now'}
                </button>
              </div>
            </div>
          ) : !isMinimized ? (
            <>
              {/* Chat history — bubble layout matching ChatWithCanvas */}
              <div
                className="flex-1 overflow-y-auto p-4 space-y-3 relative z-10"
                style={{ flex: 1, minHeight: 0 }}
              >
                {messages.length === 0 ? (
                  <div className="space-y-3">
                    <div className="flex justify-start">
                      <div className={`max-w-[80%] p-3 rounded-lg text-sm ${
                        darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-700'
                      }`}>
                        No conversation yet. Try one of these:
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 px-1">
                      {probeChips.map((chip, i) => (
                        <button
                          key={i}
                          onClick={() => handleSendWithContent(chip.message)}
                          disabled={isSending}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                            darkMode
                              ? 'bg-blue-900/40 border-blue-700 text-blue-300 hover:bg-blue-800/60'
                              : 'bg-blue-50 border-blue-200 text-blue-700 hover:bg-blue-100'
                          }`}
                        >
                          {chip.label}
                        </button>
                      ))}
                    </div>
                  </div>
                ) : (
                  messages.map(msg => (
                    <div
                      key={msg.id}
                      className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[80%] p-3 rounded-lg text-sm ${
                          msg.role === 'user'
                            ? darkMode
                              ? 'bg-blue-600 text-white'
                              : 'bg-blue-500 text-white'
                            : darkMode
                              ? 'bg-gray-700 text-white'
                              : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {msg.role === 'assistant' ? (
                          <ReactMarkdown
                            className="prose prose-sm max-w-none dark:prose-invert"
                            remarkPlugins={[remarkGfm]}
                            components={{
                              p: ({ children }) => <p className="mb-1 last:mb-0">{children}</p>,
                              ul: ({ children }) => <ul className="list-disc pl-4 mb-1">{children}</ul>,
                              ol: ({ children }) => <ol className="list-decimal pl-4 mb-1">{children}</ol>,
                              li: ({ children }) => <li className="mb-0.5">{children}</li>,
                              code: ({ children, className }) => {
                                const isBlock = className?.includes('language-');
                                return isBlock ? (
                                  <pre className="p-2 rounded bg-gray-800 text-gray-100 overflow-auto text-xs">
                                    <code>{children}</code>
                                  </pre>
                                ) : (
                                  <code className="px-1 rounded bg-gray-700 text-gray-100 text-xs">{children}</code>
                                );
                              },
                            }}
                          >
                            {msg.content}
                          </ReactMarkdown>
                        ) : (
                          <div className="whitespace-pre-wrap">{msg.content}</div>
                        )}
                        <div className="text-xs mt-1 opacity-60 text-right">
                          {msg.role === 'user' ? 'You' : personaName || 'Assistant'}
                        </div>
                      </div>
                    </div>
                  ))
                )}
                {isSending && (
                  <div className="flex justify-start">
                    <div className={`p-3 rounded-lg ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
                      <div className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500" />
                        <span className="text-sm">Thinking…</span>
                      </div>
                    </div>
                  </div>
                )}
                {/* ── Inline judgment card ─────────────────────────────── */}
                {pendingJudgmentProbeId && pendingJudgmentResponse && (() => {
                  const probe = forgeState?.probes?.find(p => p.id === pendingJudgmentProbeId);
                  return (
                    <div className={`rounded-xl border-2 p-4 mt-2 ${
                      darkMode
                        ? 'bg-amber-900/20 border-amber-600 text-amber-100'
                        : 'bg-amber-50 border-amber-400 text-amber-900'
                    }`}>
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-base">🔬</span>
                        <span className={`text-xs font-semibold uppercase tracking-wide ${
                          darkMode ? 'text-amber-300' : 'text-amber-700'
                        }`}>
                          Awaiting Judgment
                        </span>
                        {probe && (
                          <span className={`ml-auto text-xs px-1.5 py-0.5 rounded-full truncate max-w-[120px] ${
                            darkMode ? 'bg-amber-800 text-amber-200' : 'bg-amber-200 text-amber-800'
                          }`}>
                            {probe.label}
                          </span>
                        )}
                      </div>
                      {probe && (
                        <p className={`text-xs mb-2 italic ${darkMode ? 'text-amber-400' : 'text-amber-700'}`}>
                          Expected: {probe.expected_criteria}
                        </p>
                      )}
                      <input
                        type="text"
                        placeholder="Notes (optional)"
                        value={judgmentNotes}
                        onChange={e => setJudgmentNotes(e.target.value)}
                        className={`w-full p-1.5 rounded-md text-xs mb-3 ${
                          darkMode
                            ? 'bg-gray-700 text-white border border-gray-600 placeholder-gray-400'
                            : 'bg-white text-gray-800 border border-amber-300 placeholder-gray-400'
                        } focus:outline-none focus:ring-1 focus:ring-amber-400`}
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => {
                            useFabricationStore.getState().markProbeResult(
                              pendingJudgmentProbeId, true, pendingJudgmentResponse, judgmentNotes || undefined
                            );
                            setJudgmentNotes('');
                            setIsMinimized(true);
                          }}
                          className="flex-1 px-3 py-1.5 rounded-lg bg-green-600 hover:bg-green-500 text-white text-xs font-semibold shadow"
                        >
                          ✓ Pass
                        </button>
                        <button
                          onClick={() => {
                            useFabricationStore.getState().markProbeResult(
                              pendingJudgmentProbeId, false, pendingJudgmentResponse, judgmentNotes || undefined
                            );
                            setJudgmentNotes('');
                            setIsMinimized(true);
                          }}
                          className="flex-1 px-3 py-1.5 rounded-lg bg-red-600 hover:bg-red-500 text-white text-xs font-semibold shadow"
                        >
                          ✗ Fail
                        </button>
                        <button
                          onClick={() => {
                            useFabricationStore.getState().clearPendingJudgment();
                            useFabricationStore.setState(s => ({
                              probeRunStates: { ...s.probeRunStates, [pendingJudgmentProbeId]: 'idle' },
                            }));
                            setJudgmentNotes('');
                            setIsMinimized(true);
                          }}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
                            darkMode
                              ? 'bg-gray-600 hover:bg-gray-500 text-white'
                              : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                          }`}
                        >
                          Skip
                        </button>
                      </div>
                    </div>
                  );
                })()}

                <div ref={chatEndRef} />
              </div>

              {/* Input — matches ChatWithCanvas input style */}
              <div className={`p-4 border-t relative z-10 flex-shrink-0 ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className="flex space-x-2">
                  <input
                    ref={inputRef}
                    type="text"
                    value={input}
                    onChange={e => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Message your persona…"
                    disabled={isSending}
                    className={`flex-1 p-2 rounded-md border ${
                      darkMode
                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                    } focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                  />
                  <button
                    onClick={handleSend}
                    disabled={isSending || !input.trim()}
                    className={`px-3 py-2 rounded-md transition-colors ${
                      isSending || !input.trim()
                        ? darkMode
                          ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                          : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                        : darkMode
                          ? 'bg-blue-600 hover:bg-blue-700 text-white'
                          : 'bg-blue-500 hover:bg-blue-600 text-white'
                    }`}
                  >
                    {isSending ? <Spinner size="sm" /> : <FiSend size={16} />}
                  </button>
                </div>
              </div>
            </>
          ) : null}
        </div>
      )}

      {/* Pill toggle button — bottom-left, matching ChatWithCanvas position */}
      {!isPinned && (
        <button
          onClick={() => {
            if (!systemPrompt) return;
            handleOpen();
          }}
          title={pillTitle}
          style={{ position: 'fixed', bottom: '1rem', left: '1rem', zIndex: 9998 }}
          className={`flex items-center gap-2 px-4 py-2 rounded-full shadow-lg font-medium text-sm transition-all ${pillColor} ${
            !hasPrompt ? 'cursor-not-allowed opacity-60' : 'hover:opacity-90'
          } ${isOpen ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}
        >
          <FiMessageSquare size={16} />
          <span>Test Drive</span>
          {needsRecompile && hasPrompt && !isOpen && (
            <span className="w-2 h-2 rounded-full bg-amber-300 animate-pulse" />
          )}
          {messages.length > 0 && !isOpen && (
            <span className={`px-1.5 py-0.5 rounded-full text-xs font-bold ${
              darkMode ? 'bg-gray-600 text-white' : 'bg-white text-gray-800'
            }`}>
              {messages.length}
            </span>
          )}
        </button>
      )}
    </>
  );
};

export default TestDrivePanel;
